import React, { useState, useEffect } from 'react';

// Hooks
import { useSelector, useDispatch } from 'react-redux';

// Actions
import { updateTag, fetchTags } from '~/redux/actions/tags-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';

// MUI Components
import { Box, Typography, Tooltip, Stack, IconButton, MenuItem, TextField } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';

// Components
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import TruncatedText from '~/components/General/TruncatedText';
import InlineEditInput from '~/components/General/InlineEditInput';

// Icon
import { GeneralPencilCIcon, DeleteSettingCIcon, NoContactCIcon } from '~/assets/images';
import MoreVertIcon from '@mui/icons-material/MoreVert';
// Styles
import './styles.scss';

const TagsListing = (props) => {
  const {
    setEditTagModal,
    setSelectedTag,
    updatePagination,
    pagination,
    initialLoad,
    onDelete,
    isCreateModalOpen = false,
    isDeleteModalOpen = false,
  } = props;

  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeRowId, setActiveRowId] = useState(null);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNoRows, setShowNoRows] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const open = Boolean(anchorEl);
  const rows = useSelector((state) => state.tagsReducers.tags);
  const rowsCount = useSelector((state) => state.tagsReducers.rowsCount);
  const to = useSelector((state) => state.tagsReducers.to);
  const from = useSelector((state) => state.tagsReducers.from);
  const lastPage = useSelector((state) => state.tagsReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.tagsReducers.currentPage || 1);
  const perPage = useSelector((state) => state.tagsReducers.perPage) || pagination.pageSize;
  const isLoading = useSelector((state) => state.tagsReducers.loading);

  const sortedRows = [...rows].sort((a, b) => a?.title?.localeCompare(b?.title)) || [];
  const showLoader = initialLoad || isLoading || paginationLoading;
  const pageSizes = DEFAULT_PAGE_SIZES;

  // Reset pagination loading when new data arrives
  useEffect(() => {
    setPaginationLoading(false);
  }, [rows]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (row) => {
    onDelete(row);
    handleClose();
  };

  const handleEdit = (row) => {
    setEditingTagId(row.id);
    setEditingValue(row.title);
    handleClose();
  };

  const handleSave = async (newValue) => {
    const valueToSave = newValue || editingValue;
    if (!valueToSave.trim()) {
      dispatch(failure('Tag name cannot be empty'));
      return;
    }

    try {
      setSaving(true);
      const payload = { title: valueToSave.trim() };
      const response = await dispatch(updateTag(editingTagId, payload));

      if (response.status === 200) {
        dispatch(success(response.data.message || 'Tag updated successfully'));

        // Update the local state immediately to show the change
        const updatedRows = rows.map((row) =>
          row.id === editingTagId ? { ...row, title: valueToSave.trim() } : row
        );

        // Dispatch action to update the Redux state with the new data
        dispatch({
          type: 'SET_TAGS_DATA',
          payload: {
            rows: updatedRows,
            count: rowsCount,
            totalRecordsInDB: rowsCount,
            to: to,
            from: from,
            lastPage: lastPage,
            currentPage: currentPage,
            perPage: perPage,
          },
        });

        // Exit editing mode
        setEditingTagId(null);
        setEditingValue('');
      } else {
        dispatch(failure(response.data?.message || 'Failed to update tag'));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || 'Failed to update tag'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingTagId(null);
    setEditingValue('');
  };

  const handleChangePage = (event, newPage) => {
    setPaginationLoading(true);
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Tag Name',
      compactGrow: 4,
      compactMaxWidth: 480,
      // While renaming, guarantee the column always fits the inline editor
      // (input + save/cancel buttons), even when the grid renders compact
      // content-sized columns before viewport expansion runs.
      compactMinWidth: editingTagId ? 440 : undefined,
      sortable: false,
      renderCell: (params) => {
        const isEditing = editingTagId === params.row.id;

        return (
          <Box
            className="addressWrapper"
            sx={{
              width: '100%',
              overflow: 'visible',
            }}
          >
            {isEditing ? (
              <InlineEditInput
                value={editingValue}
                setValue={setEditingValue}
                handleSave={handleSave}
                handleCancel={handleCancel}
                saving={saving}
                maxLength={35}
                inputDynamicWidth={true}
                defaultWidth={250}
                maxWidth={400}
                ariaLabel="Tag name"
                errorMessage="*Tag name should be less than 35 letters"
                shouldCancel={isCreateModalOpen || isDeleteModalOpen}
                autoSelect={false}
              />
            ) : (
              <TruncatedText
                text={params.row.title}
                className={editingTagId ? 'disabledText' : ''}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'totalContacts',
      headerName: 'Total Contacts',
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.totalContacts}</Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const isMenuOpen = open && activeRowId?.id === params.row.id;
        return (
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <IconButton
              onClick={(e) => {
                setActiveRowId(params.row);
                handleClick(e);
              }}
              className="olcTableDropdownIcon"
              sx={{
                opacity: isMenuOpen ? '0.8 !important' : '0.4 !important',
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    if (sortedRows.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [sortedRows]);

  // Add delay for showing no rows overlay
  useEffect(() => {
    let timeoutId;

    if (!(initialLoad || isLoading || paginationLoading) && sortedRows.length === 0) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialLoad, isLoading, paginationLoading, sortedRows.length]);

  // Reset inline editing when pagination changes
  useEffect(() => {
    if (editingTagId !== null) {
      setEditingTagId(null);
      setEditingValue('');
      setSaving(false);
    }
  }, [currentPage, perPage]);

  return (
    <Box
      className={`olcTableGridInternal ${initialLoad || isLoading || paginationLoading ? 'table-loading' : ''} ${sortedRows.length === 0 ? 'table-empty' : ''}`}
    >
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          rows={sortedRows}
          columns={columns}
          getRowId={(row) => row.id}
          disableColumnMenu
          disableRowSelectionOnClick
          pagination={false}
          rowHeight={61}
          loading={initialLoad || isLoading || paginationLoading}
          sx={{
            width: '100%',
            '& .MuiDataGrid-cell': {
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-columnHeader': {
              overflow: 'visible !important',
            },
            ...(showNoRows ||
            (!(initialLoad || isLoading || paginationLoading) && sortedRows.length === 0)
              ? {
                  height: 'auto',
                  minHeight: '200px',
                }
              : {}),
          }}
          getRowClassName={(params) => (params.id === editingTagId ? 'row-editing' : '')}
          slots={{
            noRowsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
            noResultsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
          }}
        />
      </Box>

      {(showLoader || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) => {
            setPaginationLoading(true);
            updatePagination({ page: 1, pageSize: event.target.value });
          }}
          from={from}
          to={to}
          total={rowsCount}
        />
      )}

      {/* Menu Actions */}
      <OlcActionMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            handleEdit(activeRowId);
          }}
          disabled={editingTagId !== null}
        >
          <img src={GeneralPencilCIcon} alt="edit" />
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDelete(activeRowId);
          }}
        >
          <img src={DeleteSettingCIcon} alt="delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>
    </Box>
  );
};

export default TagsListing;
